import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { UnitOfMeasureTypes, UomText } from 'src/common/enumerations/unit-of-measure-type';
import { validateWeight } from 'src/common/helpers/weight-uom-validate';
import { IItemLookup, IQuantityPrice } from 'src/common/interfaces/dtos/IItemLookup';
import { IPriceCheckState } from 'src/common/interfaces/states/IPriceCheckState';
import { ScaleState } from 'src/common/interfaces/states/ScaleState';
import { AppState } from 'src/common/redux/configure-store';
import { ScaleStatus } from 'src/devices/device-enums';
import { SvgPromotionsActive } from '../../item-list/components/item-list-icons';
import { codeContainerStyles } from '../../common/components/code-container/components/code-container-styles';
import { truncateNumberWithPrecision } from 'src/common/helpers/number-helper';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { EntryMethod } from 'src/common/enumerations/entry-method';

const useIsMounted = () => {
  const isMountedRef = useRef(true);
  const isMounted = useCallback(() => isMountedRef.current, []);

  useEffect(() => {
    return () => void (isMountedRef.current = false);
  }, []);

  return isMounted;
};

const QuantityPriceLine: React.FC<QuantityPriceLineProps> = ({
  quantity,
  regularSalesUnitPrice,
  unitsOfMeasure,
}: QuantityPriceLineProps): JSX.Element => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  const unitPrice = regularSalesUnitPrice.price.value ?? 0;
  const regularSalesQuantity = regularSalesUnitPrice.quantity || 1;
  const itemLookup = useSelector(
    (state: AppState): IItemLookup | undefined => state.priceCheck.itemLookup,
  );

  const unitLabel: string =
    unitsOfMeasure !== UnitOfMeasureTypes.Each
      ? `/ ${UomText[unitsOfMeasure]}`
      : regularSalesQuantity > 1
      ? `/ ${regularSalesQuantity} ${t('UOMS_')}`
      : `/ ${t('DepartmentSaleView_ItemUnit')}`;
  const quantityLabel: string =
    unitsOfMeasure === UnitOfMeasureTypes.Each
      ? ` ${t('POSKeyPadControl_Qty')} `
      : ` ${UomText[unitsOfMeasure]} `;

  return quantity !== 1 ? (
    <>
      <PackPriceLine itemLookup={itemLookup} />
      <Grid
        container
        className={classes.qtyLineMargin}
        data-testid='price-check-selling-window-quantity'
      >
        <span
          data-testid='price-check-selling-window-convert-currency'
          className={`${classes.codeContainerTitleHeader} ${classes.codeContainerTitle}`}
        >
          {convertCurrency((unitPrice / regularSalesQuantity) * quantity)}
        </span>
        <span
          data-testid='price-check-selling-window-quantity-line-qty'
          className={`${classes.codeContainerTitleHeader} ${classes.quantityLinePieces} ${classes.quantityLinePadding}`}
        >
          ({quantity}
        </span>
        <span
          data-testid='price-check-selling-window-quantity-line-label'
          className={`${classes.quantityLinePieces} ${classes.codeContainerTitleHeader} ${classes.quantityLinePadding}`}
        >
          {quantityLabel}
        </span>
        <span
          className={`${classes.codeContainerTitleHeader} ${classes.quantityLinePieces} ${classes.quantityLinePadding}`}
        >
          x
        </span>
        <span
          className={`${classes.codeContainerTitleHeader} ${classes.quantityLinePieces}`}
          data-testid={`price-check-unit-price`}
        >
          {convertCurrency(unitPrice)}
        </span>
        <span
          className={`${classes.quantityLinePieces} ${classes.quantityLineColor}`}
          data-testid='price-check-selling-window-uom'
        >
          {unitLabel})
        </span>
      </Grid>
    </>
  ) : (
    <>
      <PackPriceLine itemLookup={itemLookup} />
      <Grid
        container
        className={classes.qtyLineMargin}
        data-testid='price-check-selling-window-quantity'
      >
        <span
          className={`${classes.codeContainerTitleHeader} ${classes.codeContainerTitle}`}
          data-testid={`price-check-unit-price`}
        >
          {convertCurrency(unitPrice)}
        </span>
        <span
          className={`${classes.quantityLinePieces} ${classes.quantityLineColor}`}
          data-testid='price-check-selling-window-uom'
        >
          {unitLabel}
        </span>
      </Grid>
    </>
  );
};

interface QuantityPriceLineProps {
  quantity: number;
  regularSalesUnitPrice: IQuantityPrice;
  unitsOfMeasure: string;
}

const PackPriceLine: React.FC<PackPriceProps> = ({ itemLookup }: PackPriceProps): JSX.Element => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();

  return itemLookup?.packQuantity && itemLookup.packQuantity.value ? (
    <>
      <Grid container data-testid='price-check-pack-quantity'>
        <span
          className={`${classes.codeContainerTitleHeader} ${classes.codeContainerTitle}`}
          data-testid={`price-check-pack-price`}
        >
          {convertCurrency(
            itemLookup.packQuantity.unitPrice ?? (itemLookup.packQuantity.value || 1),
          )}
        </span>
        <span
          className={`${classes.quantityLinePieces} ${classes.quantityLineColor}`}
          data-testid='price-check-pack-uom'
        >
          {`/ ${t('UOM_Pack')} `}
        </span>
        {itemLookup.unitsOfMeasure !== UnitOfMeasureTypes.Each && itemLookup.unitPrice && (
          <span
            className={`${classes.quantityLinePieces} ${classes.quantityLineColor}`}
            style={{ marginLeft: '1vh' }}
            data-testid='price-check-weighted-quantity'
          >
            {`(${truncateNumberWithPrecision(
              itemLookup.packQuantity.units ?? itemLookup.packQuantity.value / itemLookup.unitPrice,
            )} ${itemLookup.unitsOfMeasure})`}
          </span>
        )}
      </Grid>
    </>
  ) : (
    <></>
  );
};

interface PackPriceProps {
  itemLookup: IItemLookup | undefined;
}

const PriceCheckSellingWindow: React.FC<IPriceCheckSellingProps> = (
  props: IPriceCheckSellingProps,
): JSX.Element => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  const isReturnMode = useSelector(
    (state: AppState): boolean => !!state.posMode.returnMode.returnType,
  );
  const { status, weight } = useSelector((state: AppState): ScaleState => state.scale);
  const {
    queryQuantity,
    isManualWeight,
    itemLookup,
    promotions,
    hierarchy,
    entryMethod,
    itemCode,
  } = useSelector((state: AppState): IPriceCheckState => state.priceCheck);
  const isWeigtedItem = validateWeight(itemLookup?.unitsOfMeasure);
  const longDescription = itemLookup?.longDescription;
  const description = itemLookup?.description;
  const hasPrice = itemLookup?.hasPrice;
  const requiredPrice = itemLookup?.requiredPrice;
  const regularSalesUnitPrice = itemLookup?.regularSalesUnitPrice;
  const itemUom = itemLookup?.unitsOfMeasure;
  const itemId = itemLookup?.itemId;
  const [disableSellButton, setDisableSellButton] = useState<boolean>(false);
  const isMounted = useIsMounted();
  const handleSell = useCallback(async (): Promise<void> => {
    if (itemLookup) {
      setDisableSellButton(true);
      await props.onSell(itemLookup, queryQuantity, isManualWeight, entryMethod, itemCode);
      if (isMounted()) {
        setDisableSellButton(false);
      }
    }
  }, [itemLookup, props, queryQuantity, isManualWeight, entryMethod, itemCode, isMounted]);

  const unitsOfMeasure = itemUom || UnitOfMeasureTypes.Each;

  return (
    <Grid item container className={classes.sellingContainer} data-testid='price-check-Selling'>
      <Grid container className={`${classes.fitHeight} ${classes.topMargin}`}>
        <Grid item container className={classes.leftMargin}>
          <Grid container>
            <span
              className={`${classes.codeContainerTitleHeader} ${classes.descriptionStyle}`}
              data-testid='price-check-selling-window-description'
            >
              {description || longDescription}
            </span>
          </Grid>
          <Grid container className={classes.quantityContainerPadding}>
            {hasPrice && regularSalesUnitPrice ? (
              <QuantityPriceLine
                quantity={
                  !isManualWeight && isWeigtedItem && status === ScaleStatus.STABLE_WEIGHT
                    ? weight
                    : queryQuantity
                }
                regularSalesUnitPrice={regularSalesUnitPrice}
                unitsOfMeasure={unitsOfMeasure ?? ''}
              />
            ) : (
              <span
                className={`${classes.codeContainerTitleHeader} ${classes.codeContainerTitle}`}
                data-testid={requiredPrice ? `price-check-open-price` : `price-check-no-price`}
              >
                {t(requiredPrice ? 'PriceQueryView_OpenPrice' : 'PriceQueryView_NoPrice')}
              </span>
            )}
          </Grid>
        </Grid>

        <Grid item className={classes.codeContainerHorizontalSeparator}>
          <Typography />
        </Grid>
        {itemId && (
          <Grid container>
            <Grid
              item
              className={`${classes.quantityContainerPadding}  ${classes.itemCodeContainer}  ${classes.quantityLineColor} ${classes.itemCodeStyle}`}
            >
              <Typography
                data-testid={`price-check-product-id`}
                className={`${classes.itemCodeText}`}
              >
                {`${t('ItemView_Code')} ${itemId} `}
              </Typography>
              {hierarchy && (
                <Grid
                  className={classes.departmentContainer}
                  data-testid={'price-check-department'}
                >
                  {hierarchy.map(h => {
                    return (
                      <Typography key={h.id}>
                        {t('PriceQueryView_Department')} {h.id} {h.descriptions.join(',')}
                      </Typography>
                    );
                  })}
                </Grid>
              )}
            </Grid>
            {promotions && promotions.length > 0 && (
              <Grid
                data-testid='price-check-promotions'
                item
                className={classes.promotionsContainer}
              >
                <Grid container spacing={2}>
                  <Grid item>
                    <SvgPromotionsActive />
                  </Grid>
                  <Grid item>
                    <Typography data-testid='price-check-promotions-title'>
                      {t('Promotions')} {`(${promotions?.length})`}
                    </Typography>

                    {promotions.map(p => {
                      return (
                        <Typography key={p.id} className={classes.promotionDescription}>
                          {' '}
                          {p.description}{' '}
                        </Typography>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item container justifyContent='center' className={classes.selfAlign}>
        <BaseButton
          className={classes.cancelButton}
          buttonType='white'
          onClick={props.onCancel}
          data-testid='price-check-cancel-button'
        >
          {t('Cancel')}
        </BaseButton>
        {itemLookup && (
          <BaseButton
            buttonType='primary'
            onClick={handleSell}
            data-testid='price-check-sell-button'
            disabled={disableSellButton || (!hasPrice && !requiredPrice)}
          >
            {t(isReturnMode ? 'Return' : 'Sell')}
          </BaseButton>
        )}
      </Grid>
    </Grid>
  );
};

export interface IPriceCheckSellingProps {
  onSell: (
    itemData: IItemLookup,
    quantity: number,
    isManualWeight?: boolean,
    entryMethod?: EntryMethod,
    itemCode?: string,
  ) => Promise<void>;
  onCancel: () => void;
}

export default PriceCheckSellingWindow;
